import { spawn, spawnSync } from "node:child_process";
// Thin promise-based wrappers around node:fs/promises with the patterns Avatar
// uses most: ensure-dir, read-text, write-text-atomic, copy-dir-recursive.
// Atomic write writes to a temp file then renames to avoid corruption if Avatar
// crashes mid-write.
import { createHash } from "node:crypto";
import { constants, createReadStream, promises as fs } from "node:fs";
import { dirname, join, relative, sep } from "node:path";
import { Readable } from "node:stream";

export async function pathExists(path: string): Promise<boolean> {
  try {
    await fs.access(path, constants.F_OK);
    return true;
  } catch {
    return false;
  }
}

export async function ensureDir(path: string): Promise<void> {
  await fs.mkdir(path, { recursive: true });
}

export async function readText(path: string): Promise<string> {
  return await fs.readFile(path, "utf8");
}

export async function readJson<T>(path: string): Promise<T> {
  return JSON.parse(await readText(path)) as T;
}

// Atomic write: write to temp file then rename. Prevents corruption if process
// is killed between open() and close(). chmod is applied AFTER rename.
//
// Cleanup: nếu rename (hoặc chmod) fail, tmp file có thể chứa nội dung nhạy cảm
// (vd config.json với token). try/finally dọn tmp khi CHƯA rename thành công →
// không để lại file rác chứa credentials.
export async function writeTextAtomic(path: string, content: string, mode?: number): Promise<void> {
  await ensureDir(dirname(path));
  const tmp = `${path}.tmp-${process.pid}-${Date.now()}`;
  let renamed = false;
  try {
    await fs.writeFile(tmp, content, "utf8");
    if (mode !== undefined) {
      await fs.chmod(tmp, mode);
    }
    await fs.rename(tmp, path);
    renamed = true;
  } finally {
    // Rename thành công → tmp đã biến mất (rename là move). Chỉ dọn khi fail.
    if (!renamed) await fs.rm(tmp, { force: true }).catch(() => {});
  }
}

export async function writeJsonAtomic(path: string, data: unknown, mode?: number): Promise<void> {
  await writeTextAtomic(path, `${JSON.stringify(data, null, 2)}\n`, mode);
}

// Recursive copy. excludeNames filters by basename at any depth (e.g. ".git").
export async function copyDirRecursive(
  source: string,
  destination: string,
  excludeNames: string[] = [],
): Promise<void> {
  await ensureDir(destination);
  const entries = await fs.readdir(source, { withFileTypes: true });
  for (const entry of entries) {
    if (excludeNames.includes(entry.name)) continue;
    const src = join(source, entry.name);
    const dst = join(destination, entry.name);
    if (entry.isDirectory()) {
      await copyDirRecursive(src, dst, excludeNames);
    } else if (entry.isSymbolicLink()) {
      const target = await fs.readlink(src);
      await fs.symlink(target, dst);
    } else {
      await fs.copyFile(src, dst);
    }
  }
}

export async function removeRecursive(path: string): Promise<void> {
  await fs.rm(path, { recursive: true, force: true });
}

export function relativeFromCwd(path: string): string {
  return relative(process.cwd(), path);
}

/**
 * Atomically replace `targetDir` with `stagedDir` on the same filesystem.
 * Keeps the old dir until the new one is in place; rolls back on failure.
 * Pure filesystem — no network. Shared by the Supabase download path and the
 * `--local` install path so both get identical swap semantics.
 */
export async function atomicSwapDir(stagedDir: string, targetDir: string): Promise<void> {
  const backupDir = `${targetDir}.old-${process.pid}`;
  const hadOld = await pathExists(targetDir);
  if (hadOld) {
    await fs.rename(targetDir, backupDir); // old → backup (same fs, atomic)
  }
  try {
    await fs.rename(stagedDir, targetDir); // new → in place
  } catch (err) {
    if (hadOld) {
      await fs.rename(backupDir, targetDir).catch(() => {});
    }
    throw err;
  }
  if (hadOld) {
    await removeRecursive(backupDir).catch(() => {});
  }
}

// ─── Pack download/extract helpers (v3: tải tarball pack từ Supabase) ────────

// Tải file từ URL về `dest`. Stream body ra file (không nạp hết vào memory).
// S3: AbortController timeout (default 300s = thời hạn signed URL Supabase) +
// dọn partial file nếu fail giữa chừng. Tránh treo vô hạn / để lại file rác.
const DOWNLOAD_TIMEOUT_MS = 300_000;

// VULN-4b: cap kích thước download để chặn decompression/download-bomb (server
// trả file khổng lồ làm đầy disk). Check Content-Length SỚM (nếu server có gửi)
// NHƯNG cũng phải đếm byte thực tế stream qua vì header có thể thiếu hoặc bị
// giả mạo (server không đáng tin trả header sai). 100MB dư sức cho pack thật
// (vài chục MB nén).
const MAX_DOWNLOAD_BYTES = 100 * 1024 * 1024;

export async function downloadFile(
  url: string,
  dest: string,
  timeoutMs = DOWNLOAD_TIMEOUT_MS,
): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  await ensureDir(dirname(dest));
  const { createWriteStream } = await import("node:fs");

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) {
      throw new Error(`Download fail (${res.status}) từ ${url.slice(0, 80)}...`);
    }
    if (!res.body) {
      throw new Error("Response không có body để tải.");
    }
    const contentLength = Number(res.headers.get("content-length") ?? "");
    if (Number.isFinite(contentLength) && contentLength > MAX_DOWNLOAD_BYTES) {
      throw new Error(
        `File quá lớn (Content-Length ${contentLength} bytes), vượt giới hạn ${MAX_DOWNLOAD_BYTES} bytes. Từ chối tải.`,
      );
    }
    const fileStream = createWriteStream(dest);
    await new Promise<void>((resolve, reject) => {
      const src = Readable.fromWeb(res.body as Parameters<typeof Readable.fromWeb>[0]);
      let bytesRead = 0;
      src.on("data", (chunk: Buffer) => {
        bytesRead += chunk.length;
        // Content-Length có thể thiếu/giả mạo — đếm byte thực tế stream qua và
        // abort mid-stream nếu vượt cap, thay vì tin tưởng tuyệt đối vào header.
        if (bytesRead > MAX_DOWNLOAD_BYTES) {
          src.destroy(
            new Error(
              `File vượt giới hạn ${MAX_DOWNLOAD_BYTES} bytes giữa chừng tải. Từ chối tải.`,
            ),
          );
        }
      });
      src.on("error", reject); // S-fix: bắt lỗi mid-stream của source.
      fileStream.on("error", reject);
      fileStream.on("finish", () => resolve());
      src.pipe(fileStream);
    });
  } catch (err) {
    // Dọn partial file để không để lại tarball hỏng.
    await fs.rm(dest, { force: true }).catch(() => {});
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(`Tải file quá ${timeoutMs / 1000}s (signed URL có thể đã hết hạn).`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// Giải nén tarball .tar.gz vào `destDir` bằng system `tar`. macOS/Linux có sẵn.
// S4: Windows mặc định không có `tar` ở mọi phiên bản → probe trước, throw rõ ràng.
let tarAvailableCache: boolean | null = null;
function tarAvailable(): boolean {
  if (tarAvailableCache !== null) return tarAvailableCache;
  const r = spawnSync("tar", ["--version"], { stdio: "ignore" });
  tarAvailableCache = r.status === 0;
  return tarAvailableCache;
}

// SECURITY (path-traversal): TRƯỚC khi extract, liệt kê nội dung tarball (`tar -tzf`)
// và validate MỌI entry path nằm trong destDir. Chặn tarball độc chứa absolute path
// (`/etc/...`) hoặc `../` escape ghi đè file ngoài destDir (vd ~/.zshrc, ~/.ssh).
// Verify trước → extract sau (tar -tzf chỉ đọc, không ghi gì).
//
// VULN-4c: cap số entry để chặn decompression-bomb dạng "triệu file rỗng" làm
// nghẽn disk/inode dù dung lượng nén nhỏ. Pack thật có vài trăm file — 5000 dư sức.
const MAX_TARBALL_ENTRIES = 5000;

async function listTarballEntries(tarball: string): Promise<string[]> {
  const entries = await new Promise<string[]>((resolve, reject) => {
    const child = spawn("tar", ["-tzf", tarball], { stdio: ["ignore", "pipe", "pipe"] });
    let stdout = "";
    let stderr = "";
    child.stdout.on("data", (c) => {
      stdout += c.toString();
    });
    child.stderr.on("data", (c) => {
      stderr += c.toString();
    });
    child.on("error", reject);
    child.on("close", (code) => {
      if (code === 0) resolve(stdout.split("\n").filter((l) => l.trim().length > 0));
      else reject(new Error(`tar list exit ${code}: ${stderr.trim()}`));
    });
  });
  if (entries.length > MAX_TARBALL_ENTRIES) {
    throw new Error(
      `Tarball có ${entries.length} entries, vượt giới hạn ${MAX_TARBALL_ENTRIES}. Từ chối giải nén (nghi decompression bomb).`,
    );
  }
  return entries;
}

// Một entry an toàn nếu: không absolute (/...), không bắt đầu ~, và sau khi join
// với destDir vẫn nằm TRONG destDir (chặn ../ escape).
function isEntryWithinDest(entry: string, destDir: string): boolean {
  if (entry.startsWith("/") || entry.startsWith("~")) return false;
  const resolved = join(destDir, entry);
  const rel = relative(destDir, resolved);
  // rel bắt đầu ".." nghĩa là escape ra ngoài destDir.
  return rel !== ".." && !rel.startsWith(`..${sep}`);
}

// VULN-3 (TOCTOU symlink escape): listTarballEntries/isEntryWithinDest chỉ check
// TÊN path (name-only `tar -tzf`, không có type flag) — 1 symlink entry
// `foo -> /etc/passwd` có path "foo" (nằm an toàn trong destDir) nên pass gate
// path-validate, nhưng sau khi extract, `foo` là symlink trỏ RA NGOÀI destDir.
// Code sau đó đọc file theo path extracted sẽ dereference ra ngoài — escape.
//
// Fix: post-extraction lstat-walk (không parse `tar -tvzf` verbose vì format
// gnutar/bsdtar khác nhau, fragile cross-platform). Walk đệ quy destDir bằng
// fs.lstat (KHÔNG follow symlink) sau khi tar -xzf xong — nếu thấy bất kỳ entry
// nào isSymbolicLink() thì throw. Pack tarball chỉ chứa source file, không có
// use-case hợp lệ nào cần symlink → reject toàn bộ, không cần phân biệt "trỏ
// trong/ngoài destDir".
async function rejectSymlinksInExtractedDir(destDir: string): Promise<void> {
  const found: string[] = [];
  async function walk(dir: string): Promise<void> {
    const entries = await fs.readdir(dir, { withFileTypes: true });
    for (const entry of entries) {
      const entryPath = join(dir, entry.name);
      const stat = await fs.lstat(entryPath);
      if (stat.isSymbolicLink()) {
        found.push(entryPath);
        continue;
      }
      if (stat.isDirectory()) {
        await walk(entryPath);
      }
    }
  }
  await walk(destDir);
  if (found.length > 0) {
    throw new Error(
      `Tarball chứa symlink sau khi giải nén (không được phép): ${found.join(", ")}. ` +
        "Pack chỉ chứa source file, symlink có thể trỏ ra ngoài thư mục đích (TOCTOU escape).",
    );
  }
}

export async function extractTarballToDir(tarball: string, destDir: string): Promise<void> {
  if (!tarAvailable()) {
    throw new Error(
      "Không tìm thấy lệnh `tar` trên hệ thống. Cần để giải nén pack.\n" +
        "  Windows: cài qua WSL2, Git Bash, hoặc MSYS2.\n" +
        "  macOS/Linux: tar thường có sẵn — kiểm tra PATH.",
    );
  }
  await ensureDir(destDir);

  // SECURITY gate: validate path scope trước khi ghi bất cứ gì (defense-in-depth,
  // giữ lại dù chưa bắt được symlink-type — vẫn chặn absolute/`../` path khác).
  const entries = await listTarballEntries(tarball);
  const unsafe = entries.find((e) => !isEntryWithinDest(e, destDir));
  if (unsafe) {
    throw new Error(
      `Tarball chứa path không an toàn (path-traversal): "${unsafe}". Từ chối giải nén để tránh ghi đè file ngoài thư mục đích.`,
    );
  }

  await new Promise<void>((resolve, reject) => {
    // Chỉ dựa vào gate path-validate phía trên (cross-platform). KHÔNG dùng
    // --no-absolute-filenames vì macOS bsdtar không nhận flag này → fail extract.
    const child = spawn("tar", ["-xzf", tarball, "-C", destDir], {
      stdio: ["ignore", "ignore", "pipe"],
    });
    let stderr = "";
    child.stderr.on("data", (chunk) => {
      stderr += chunk.toString();
    });
    child.on("error", reject);
    child.on("close", (code) => {
      if (code === 0) resolve();
      else reject(new Error(`tar extract exit ${code}: ${stderr.trim()}`));
    });
  });

  // VULN-3 gate: post-extraction, reject bất kỳ symlink nào lọt qua tar thật.
  await rejectSymlinksInExtractedDir(destDir);
}

// Tính SHA-256 của file (hex). Để verify integrity tarball trước khi giải nén.
// CHƯA WIRE (không export — tránh hiểu nhầm verify đã bật): get-pack endpoint
// Supabase HIỆN chưa trả sha256. TODO supply-chain (mini-project riêng): build-
// and-upload script tính sha lúc upload → lưu metadata bucket → get-pack trả về
// → downloadAndExtractPack nhận expectedSha, so với hàm này TRƯỚC extract.
// Verify hiện dựa HTTPS + signed URL TTL 5p từ bucket private (đủ cho hiện tại).
// biome-ignore lint/correctness/noUnusedVariables: placeholder cố ý cho supply-chain verify, chưa wire (xem comment trên)
async function computeFileSha256(path: string): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const hash = createHash("sha256");
    const stream = createReadStream(path);
    stream.on("data", (chunk) => hash.update(chunk));
    stream.on("end", () => resolve(hash.digest("hex")));
    stream.on("error", reject);
  });
}
